import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { AlertTriangle, Search } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const levelBadge = {
  level_1: { label: 'CPD Recognized', className: 'bg-blue-100 text-blue-800' },
  level_2: { label: 'CPD Quality Assured', className: 'bg-[#C5A572]/20 text-[#9A7D4E]' },
  level_3: { label: 'Advanced CPD Recognized', className: 'bg-[#1B2A4A] text-white' },
};

const statusBadge = {
  active: 'bg-green-100 text-green-700',
  expired: 'bg-red-100 text-red-700',
  under_review: 'bg-amber-100 text-amber-700',
};

export default function CPDRecognizedActivitiesPage() {
  const [activities, setActivities] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchActivities = async () => {
      try {
        const res = await fetch(`${API}/api/cpd-recognition/activities`);
        if (res.ok) {
          const data = await res.json();
          setActivities(data);
        }
      } catch {
        // API not available, show empty state
      } finally {
        setLoading(false);
      }
    };
    fetchActivities();
  }, []);

  const filtered = activities.filter(a =>
    a.organization_name?.toLowerCase().includes(search.toLowerCase()) ||
    a.program_name?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div data-testid="cpd-recognized-activities-page">
      <section className="bg-[#1B2A4A] py-16 md:py-20">
        <div className="imcac-container">
          <p className="imcac-overline mb-3">CPD Recognition</p>
          <h1 className="text-4xl sm:text-5xl font-bold text-white tracking-tight font-heading">Recognized Activities</h1>
          <div className="h-1 w-20 bg-[#C5A572] mt-5" />
          <p className="text-lg text-slate-300 mt-4 max-w-3xl">
            Public directory of educational activities that have received IMCAC CPD Recognition.
          </p>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="bg-[#F8FAFC] border-b border-slate-200 py-5">
        <div className="imcac-container">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-[#C5A572] flex-shrink-0 mt-0.5" />
            <p className="text-sm text-slate-600 leading-relaxed">
              IMCAC CPD Recognition evaluates educational activities for Continuing Professional Development purposes only. Recognition does not constitute governmental approval, university accreditation, specialist recognition, professional licensure, degree granting authority, or regulatory endorsement.
            </p>
          </div>
        </div>
      </section>

      <section className="imcac-section bg-white">
        <div className="imcac-container">
          {/* Search */}
          <div className="relative mb-8 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search by organization or program name..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 rounded-sm"
              data-testid="search-activities"
            />
          </div>

          {loading ? (
            <div className="text-center py-16 text-slate-500">
              <p>Loading recognized activities...</p>
            </div>
          ) : filtered.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="activities-table">
                <thead>
                  <tr className="bg-[#1B2A4A] text-white">
                    <th className="p-3 text-left font-medium">Organization</th>
                    <th className="p-3 text-left font-medium">Program Name</th>
                    <th className="p-3 text-left font-medium">Recognition Level</th>
                    <th className="p-3 text-left font-medium">CPD Credits</th>
                    <th className="p-3 text-left font-medium">Recognition Period</th>
                    <th className="p-3 text-left font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((activity, i) => {
                    const level = levelBadge[activity.recognition_level] || levelBadge.level_1;
                    const status = statusBadge[activity.status] || statusBadge.active;
                    return (
                      <tr key={activity.id || i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                        <td className="p-3 text-slate-700 font-medium">{activity.organization_name}</td>
                        <td className="p-3 text-slate-700">{activity.program_name}</td>
                        <td className="p-3"><Badge className={`${level.className} text-xs`}>{level.label}</Badge></td>
                        <td className="p-3 text-slate-700 font-medium">{activity.cpd_credits}</td>
                        <td className="p-3 text-slate-500 text-xs">{activity.recognition_period}</td>
                        <td className="p-3"><Badge className={`${status} text-xs capitalize`}>{activity.status?.replace('_', ' ')}</Badge></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <Card className="imcac-card">
              <CardContent className="p-12 text-center">
                <Search className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                <h3 className="text-lg font-semibold text-[#1B2A4A] font-heading mb-2">
                  {search ? 'No results found' : 'Directory Coming Soon'}
                </h3>
                <p className="text-sm text-slate-500 max-w-md mx-auto">
                  {search
                    ? 'Try adjusting your search terms.'
                    : 'Recognized activities will appear here as applications are reviewed and approved. Organizations may apply for CPD Recognition through the application process.'}
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </section>
    </div>
  );
}
